import { useState, useCallback, FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "components/Modal";
import Spinner from "components/Spinner";
import Button from "components/Button";
import { AdditionalOptionsTextBox } from "components/hosted-scraping/edit-project-components/AdditionalOptionsTextBox";
import { isValidUrl } from "utils/urlUtils";
import { path as HomePath } from "v2/pages/home/HomeRoute";
import { DataTable, Column } from "components/Table";
import scraperApi from "api";
import type { DomainMultiplierResponse } from "utils/legacyRate";

type MultiplierRow = {
  label: string;
  credits: number;
};

const COLUMNS: Column<MultiplierRow>[] = [
  { key: "label", header: "Type", cell: (row) => row.label },
  {
    key: "credits",
    header: "Credits",
    align: "right",
    cell: (row) => row.credits,
    cellClassName: "text-neutral-900 font-medium",
  },
];

/*
 * The endpoint takes a domain, so requiring a scheme is just friction - accept
 * "amazon.co.uk" as readily as "https://www.amazon.co.uk/dp/B01".
 */
function withScheme(input: string): string {
  return /^https?:\/\//i.test(input) ? input : `https://${input}`;
}

/*
 * Checks the input is a domain we can quote, returning it or null. Rejects bare
 * hostnames ("localhost") and IPs, which isValidUrl allows. A made-up TLD gets
 * through - "www.eeeee" looks just like "www.google" - that needs the backend.
 */
function toDomain(input: string): string | null {
  const candidate = withScheme(input);
  if (!isValidUrl(candidate)) return null;

  const { hostname } = new URL(candidate);
  if (!/\.[a-z]{2,}$/i.test(hostname)) return null;

  return hostname.replace(/^www\./, "");
}

export default function DomainMultiplierModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location?.state as { backgroundLocation?: Location };

  const goBack = useCallback(() => {
    // In-app overlay has history to step back to; a hard-load doesn't, so go Home.
    if (state?.backgroundLocation) {
      navigate(-1);
    } else {
      navigate(HomePath, { replace: true });
    }
  }, [navigate, state?.backgroundLocation]);
  
  const [searchInput, setSearchInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<DomainMultiplierResponse | null>(null);

  const handleSearch = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      if (isLoading) return;
      setValidationError(null);

      const trimmed = searchInput.trim();
      if (!trimmed) return;

      const domain = toDomain(trimmed);

      if (!domain) {
        setResult(null);
        setError(null);
        setValidationError("Please enter a valid domain (e.g. amazon.com)");
        return;
      }

      setIsLoading(true);
      setError(null);
      setResult(null);

      try {
        const data = await scraperApi.multipliers.getDomainMultiplier(domain);
        setResult(data);
      } catch {
        /*
         * The backend rejects domains it can't quote (unknown TLD), but that
         * 400 reaches us as a 500 - the server's response handler drops the
         * status - so one message has to cover both causes honestly.
         */
        setError(
          "Couldn't get credit costs. Check the domain is correct, or try again shortly.",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [searchInput, isLoading],
  );

  const handleInputChange = useCallback((value: string | undefined) => {
    setSearchInput(value || "");
    setValidationError(null);
  }, []);

  return (
    <Modal headline="Domain Credit Costs" onClose={goBack}>
      <div className="px-5 py-4">
        <p className="text-sm text-neutral-600 mb-4">
          Search for a domain to see how many API Credits are required to scrape
          it.
        </p>

        <form onSubmit={handleSearch} className="flex items-center gap-2 mb-4">
          <AdditionalOptionsTextBox
            value={searchInput}
            placeholder="e.g. amazon.com"
            callback={handleInputChange}
          />
          <Button
            text="Search"
            onClick={handleSearch}
            className="button button-primary text-sm"
            disabled={!searchInput.trim() || isLoading}
          />
        </form>

        <div className="mb-4">
          <a
            href="https://docs.scraperapi.com/getting-started/quick-start/credits-and-requests-costs"
            className="text-gray-700 pb-4 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Guide to our scraping costs
          </a>
        </div>

        {validationError && (
          <p className="text-red-500 text-sm mb-4">{validationError}</p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner className="w-5 h-5 animate-spin m-3 text-neutral-500" />
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 text-center py-4">{error}</div>
        )}

        {result && (
          <>
            <p className="text-sm text-neutral-600 mb-2">
              Credit costs for{" "}
              <span className="font-medium text-neutral-900">
                {result.domain}
              </span>
            </p>
            <DataTable
              columns={COLUMNS}
              // List price for everyone; grandfathered accounts learn their rate from the legacy message (ENG-1112).
              rows={Object.entries(
                result.standardMultipliers ?? result.multipliers,
              ).map(([label, credits]) => ({ label, credits }))}
              getRowKey={(row) => row.label}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
